import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { showToast, toastStore, type ITooltipProps } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { RequestLoadData } from 'shared/api/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseUser } from 'entities/Users/api/types'
import { type IUser } from 'entities/Users'
import { User } from 'entities/Users/model/User'
import { TeamsApi } from 'entities/Teams/api/teams'
import {
  type IParamsTeamsPerPage,
  Team,
  type IResponseTeam,
  DeleteTeamModalStore,
} from 'entities/Teams'
import { TeamRow } from 'entities/Teams/model/TeamRow'
import { type IResponseIntegrationTeam } from 'entities/Inbox/api/types'
import { IntegrationKey } from 'entities/Integrations'
import { AddMemberStore, type IAddMemberStoreConfig } from 'widgets/AddMembers'
import { getMembersTableColumns } from 'pages/settings/pages/teams/ui/ManageTeamMembers/getMembersTableColumns'
import { MemberRow } from 'pages/settings/pages/teams/ui/ManageTeamMembers/MemberRow'
import { TeamsPath } from 'pages/settings/pages/teams/route/teamsPath'
import { SettingsRoutes } from 'pages/settings/route/type'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'
import { RenameTeamModalActions } from 'pages/settings/pages/teams/ui/RenameTeamModal/RenameTeamModalActions'
import { GoToQuestionnaireModalStore } from 'pages/settings/pages/compliance/ui/GoToQuestionnaireModal'
import { RenameTeamModalContent } from 'pages/settings/pages/teams/ui/RenameTeamModal/RenameTeamModalContent'

export class TeamsStore {
  private readonly _tableStore: TableStore<TeamRow>
  private readonly _membersTableStore: TableStore<MemberRow>
  private readonly _membersTableColumns = getMembersTableColumns()
  private _itemsMap: Map<number, Team> = new Map()
  private _search = ''
  private _integrationTeams: IResponseIntegrationTeam[] = []
  private _importingFromHubSpot = false
  private _refreshingIntegrationTeamsData = false
  private _noTeamsToImportModalId = 'teams_no_teams_to_import_modal'
  private _renameTeamModalId = 'teams_rename_team_modal'
  private _renameTeam: Team | null = null
  private _renameTeamName = ''
  private _renameTeamDescription = ''
  private _renameTeamLoading = false
  private _resyncingIntegrationTeams = false
  private _resyncingIntegrationTeamId: number | null = null

  private _addMembersTeamId: number | null = null
  private _currentTeam: Team | null = null
  /** Success toasts with Undo on manage-members; cleared on `init` / team change so stale actions cannot run. */
  private _toastIds: string[] = []

  private _disposeReaction: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    this._tableStore = new TableStore({
      key: 'teamsTable',
      element: 'team',
      sortOrder: 'desc',
      sortBy: 'updated_at',
      useColumnsCache: true,
      limit: 10,
    })
    this._membersTableStore = new TableStore<MemberRow>({
      key: 'manageTeamMembersTable',
      element: 'member',
      sortBy: 'fullName',
      sortOrder: 'asc',
    })

    makeAutoObservable(this)

    this._disposeReaction = reaction(
      () => this._listRequestParams,
      () => {
        this._requestLoadData.debounceLoadData()
      }
    )
  }

  private clearTrackedManageMembersToasts = (): void => {
    for (const id of this._toastIds) {
      toastStore.remove(id)
    }
    this._toastIds = []
  }

  private trackManageMembersToastId = (id: string): void => {
    this._toastIds.push(id)
  }

  private untrackManageMembersToastId = (id: string): void => {
    this._toastIds = this._toastIds.filter((t) => t !== id)
  }

  init = async () => {
    this.clearTrackedManageMembersToasts()

    runInAction(() => {
      this._currentTeam = null
    })
    await Promise.all([this.loadData(), this.loadIntegrationTeams()])
  }

  initManageTeamMembers = async (teamId: number): Promise<void> => {
    this.clearTrackedManageMembersToasts()
    runInAction(() => {
      this._currentTeam = null
    })
    await this.fetchTeamById(teamId)
  }

  get currentTeam(): Team | null {
    return this._currentTeam
  }

  get currentTeamId() {
    return this._currentTeam?.id
  }

  loadIntegrationTeams = async () => {
    try {
      const { data } = await this._entities.inboxesStore.getIntegrationTeams()

      runInAction(() => {
        this._integrationTeams = data ?? []
      })
    } catch (error) {
      logger.error(error)
    }
  }

  get _listRequestParams() {
    return { ...this.tableStore.params, search: this._search }
  }

  get isEmpty() {
    return !this._tableStore.loading && !this._itemsMap.size && !this._search.trim()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  getItem = (id: number) => {
    return this.items.find((item) => item.id === id)
  }

  get itemsRows() {
    return this.items.map((item) => new TeamRow(item.origin))
  }

  get tableStore() {
    return this._tableStore
  }

  get membersTableStore() {
    return this._membersTableStore
  }

  resetAllTableSelections = () => {
    this._tableStore.bulkStore.resetSelected()
    this._membersTableStore.bulkStore.resetSelected()
  }

  get membersTableColumns() {
    return this._membersTableColumns
  }

  get loading() {
    return this._tableStore.loading
  }

  get entities() {
    return this._entities
  }

  getIsLastMember = (team: Team): boolean => {
    return (team.members ?? []).length === 1
  }

  getRemoveTooltipProps = (team: Team): ITooltipProps => {
    const isLastMember = this.getIsLastMember(team)
    return isLastMember
      ? {
          type: 'description',
          label: "Can't remove last member",
          desc: 'Teams must have at least one member',
          placement: 'top',
        }
      : { label: 'Remove member', placement: 'top' }
  }

  getIsAllMembersAdded = (team: Team): boolean => {
    if (team.isIntegrationTeam) return false
    const membersList = this._entities.usersStore.membersList
    if (membersList.length === 0) return false
    const teamMemberIds = new Set((team.members ?? []).map((m) => m.id))
    return membersList.every((m) => teamMemberIds.has(m.id))
  }

  private _getAllOrganizationMembersAsIResponseUser = (): IResponseUser[] => {
    return this._entities.usersStore.membersList.map((m) => this._userToResponseUser(m))
  }

  private _userToResponseUser(m: IUser): IResponseUser {
    if (m instanceof User) return m.origin
    return {
      id: m.id,
      first_name: m.first_name,
      last_name: m.last_name,
      full_name: m.full_name,
      email: m.email,
      photo_url: m.photo_url ?? '',
      timezone: null,
      role: m.role,
      role_label: m.role_label,
      permissions: m.permissions ?? null,
      disabled: null,
      has_inbox_access_request: false,
      has_change_role_request: false,
      member_teams_count: 0,
      default_team_id: null,
      two_fa_enabled: false,
      formatted_number: '',
      item_type: 'user',
      isAway: m.isAway,
      first_promoter_ref_id: null,
      team_role: 'member',
    }
  }

  submitAddMembers = async (selected: IResponseUser[]): Promise<void> => {
    if (this._addMembersTeamId === null || selected.length === 0 || !this.currentTeamId) return

    const memberIdsToAdd = selected.map((m) => m.id)

    const { data } = await this._entities.inboxesStore.addGroupMembers(
      this.currentTeamId,
      memberIdsToAdd
    )

    runInAction(() => {
      this._currentTeam = new Team(data.data)
    })
  }

  clearAddMembersContext = (): void => {
    this._addMembersTeamId = null
  }

  openAddMembersModal = (team: Team): void => {
    if (!this._entities.complianceStore.isQuestionnaire) {
      new GoToQuestionnaireModalStore(this._entities).open('members')
      return
    }
    this._addMembersTeamId = team.origin.id
    const config: IAddMemberStoreConfig<IResponseUser> = {
      getExistingMemberIds: () => {
        return this.currentTeam?.origin.members.map((m) => m.id) ?? []
      },
      getOrganizationMembers: () => this._getAllOrganizationMembersAsIResponseUser(),
      getOrganizationMembersLoading: () => this._entities.usersStore.loading,
      submitAddMembers: async (selected) => {
        await this.submitAddMembers(selected)
      },
      onClear: () => {
        this.clearAddMembersContext()
      },
      successMessage: 'Members added to team',
      modalId: 'teams-add-members-modal',
    }
    const addMemberStore = new AddMemberStore<IResponseUser>(config)
    addMemberStore.openAddMembersModal()
  }

  getMembersRows = (team: Team): MemberRow[] => {
    const rows = (team.members ?? []).map((member) => new MemberRow(member))
    if (!team.isIntegrationTeam) {
      const isLastMember = this.getIsLastMember(team)
      const removeTooltipProps = this.getRemoveTooltipProps(team)
      rows.forEach((row) => {
        row.setActionsProps({
          actions: [
            {
              icon: 'close',
              text: 'Remove',
              disabled: isLastMember,
              iconButtonProps: {
                fontSize: 16,
                size: 'small',
                disabled: isLastMember,
                ariaLabel: 'ManageTeamMembersRemoveMember',
                tooltipProps: removeTooltipProps,
              },
              onAction: () => this.handleRemoveMember(row.origin.id),
            },
          ],
          showItems: 1,
        })
      })
    }
    return rows
  }

  getFilteredMembersRows = (rows: MemberRow[], searchQuery: string): MemberRow[] => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return rows
    return rows.filter(
      (row) => row.fullName.toLowerCase().includes(query) || row.email.toLowerCase().includes(query)
    )
  }

  getSortedAndFilteredMembersRows = (rows: MemberRow[], searchQuery: string): MemberRow[] => {
    const filtered = this.getFilteredMembersRows(rows, searchQuery)
    const sortBy = this._membersTableStore.sortBy
    const sortOrder = this._membersTableStore.sortOrder
    if (!sortBy) return filtered
    const getSortValue = (row: MemberRow, key: string): string => {
      if (key === 'fullName') return row.fullName
      if (key === 'email') return row.email
      return String(row.id)
    }
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortBy).toLowerCase()
      const bVal = getSortValue(b, sortBy).toLowerCase()
      const cmp = aVal.localeCompare(bVal)
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }

  get search() {
    return this._search
  }

  get isSearchEmpty() {
    return this._search.trim() === ''
  }

  setSearch = (value: string) => {
    this._tableStore.setPage(1)
    this._search = value
    if (this.isSearchEmpty) this._tableStore.setLoading(true)
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const params: IParamsTeamsPerPage = {
        ...this._tableStore.params,
        search: this._search,
        sortBy: this._tableStore.sortBy,
      }

      if (this.isSearchEmpty) {
        delete params.search
      }

      const { data } = await TeamsApi.getTeamsPerPage(params, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          this.setItem(item)
        })

        this._tableStore.setTotal(data.meta.total)
        if (data.meta.current_page !== this._tableStore.page) {
          this._tableStore.setPage(data.meta.current_page)
        }
        if (data.meta.per_page !== this._tableStore.limit) {
          this._tableStore.setLimit(data.meta.per_page)
        }
        this._tableStore.bulkStore.resetSelected()
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    },
  })

  loadData = this._requestLoadData.loadData

  openDeleteModal = (id: number, isIntegrationTeam: boolean, onDeleted?: () => void) => {
    new DeleteTeamModalStore(id, isIntegrationTeam, () => {
      runInAction(async () => {
        if (this._itemsMap.size === 1) {
          if (this._tableStore.page === 1) {
            await this.loadData()
          } else {
            this._tableStore.setPage(this._tableStore.page - 1)
          }
        } else {
          this._tableStore.setTotal(this._tableStore.total - 1)
          this._itemsMap.delete(id)
          this._tableStore.bulkStore.resetSelected()
          await this.loadData()
        }
        onDeleted?.()
      })
    })
  }

  setItem = (data: IResponseTeam) => {
    this._itemsMap.set(data.id, new Team(data))
  }

  private fetchTeamById = async (teamId: number) => {
    try {
      const { data } = await TeamsApi.getTeam(teamId)

      runInAction(() => {
        this._currentTeam = new Team(data.data)
      })
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this._currentTeam = null
      })
    }
  }

  navigateToCreateTeam = () => {
    uiStore.changeRoute({ path: TeamsPath.create })
  }

  get integrationTeams() {
    return this._integrationTeams
  }

  get isHubSpotConnected() {
    const hubspot = this._entities.integrationsStore.getIntegration(IntegrationKey.hubspot)

    return hubspot?.isConnected ?? false
  }

  get hasIntegrationTeamsToImport() {
    return this._integrationTeams.length > 0
  }

  get showImportFromHubSpotAsSelect() {
    return this.isHubSpotConnected && this.hasIntegrationTeamsToImport
  }

  get importHubSpotDropdownItems() {
    return this._integrationTeams.map((team) => ({
      id: team.id,
      label: team.name,
      searchPattern: team.name,
    }))
  }

  handleImportAllTeamsFromHubSpot = async (onClose?: () => void): Promise<void> => {
    runInAction(() => {
      onClose?.()
      this._importingFromHubSpot = true
    })
    try {
      await this._entities.inboxesStore.importIntegrationTeams()
      await Promise.all([this.loadData(), this.loadIntegrationTeams()])
      showToast({ type: 'success', title: 'Teams imported' })
    } catch (error) {
      showToast({ type: 'error', title: 'Import from HubSpot failed' })
      logger.error(error)
    } finally {
      runInAction(() => {
        this._importingFromHubSpot = false
      })
    }
  }

  get importingFromHubSpot() {
    return this._importingFromHubSpot
  }

  get refreshingIntegrationTeamsData() {
    return this._refreshingIntegrationTeamsData
  }

  handleRefreshIntegrationTeamsData = async (): Promise<void> => {
    runInAction(() => {
      this._refreshingIntegrationTeamsData = true
    })
    try {
      await this._entities.inboxesStore.refreshIntegrationTeamsList()
      await this.loadIntegrationTeams()

      showToast({ type: 'success', title: 'Data refreshed' })
    } catch (error: unknown) {
      showToast({ type: 'error', title: 'Refresh failed' })
      logger.error(error)
    } finally {
      runInAction(() => {
        this._refreshingIntegrationTeamsData = false
      })
    }
  }

  handleImportIntegrationTeam = async (
    integrationTeamId: number,
    onClose?: () => void
  ): Promise<void> => {
    runInAction(() => {
      onClose?.()
      this._importingFromHubSpot = true
    })
    try {
      await this._entities.inboxesStore.createIntegrationGroup({
        integration_team_id: integrationTeamId,
      })
      await Promise.all([this.loadData(), this.loadIntegrationTeams()])
      showToast({ type: 'success', title: 'Team imported' })
    } catch (error) {
      showToast({ type: 'error', title: 'Import from HubSpot failed' })
      logger.error(error)
    } finally {
      runInAction(() => {
        this._importingFromHubSpot = false
      })
    }
  }

  handleResyncIntegrationTeam = async (
    teamId: number,
    fromManageMembers = false
  ): Promise<void> => {
    runInAction(() => {
      this._resyncingIntegrationTeamId = teamId
    })
    try {
      await Promise.all([
        this._entities.inboxesStore.refreshIntegrationTeams(teamId),
        this._entities.inboxesStore.refreshIntegrationTeamContacts(teamId),
      ])

      fromManageMembers ? await this.fetchTeamById(teamId) : await this.loadData()

      showToast({ type: 'success', title: 'Hubspot data refreshed' })
    } catch (error) {
      showToast({ type: 'error', title: 'Hubspot data refresh failed' })
      logger.error(error)
    } finally {
      runInAction(() => {
        this._resyncingIntegrationTeamId = null
      })
    }
  }

  getResyncingIntegrationTeam = (teamId: number): boolean => {
    return this._resyncingIntegrationTeamId === teamId
  }

  get resyncingIntegrationTeams() {
    return this._resyncingIntegrationTeams
  }

  handleResyncIntegrationTeams = async (teamIds: number[]): Promise<void> => {
    if (teamIds.length === 0) return
    runInAction(() => {
      this._resyncingIntegrationTeams = true
    })
    try {
      await Promise.all(
        teamIds.flatMap((teamId) => [
          this._entities.inboxesStore.refreshIntegrationTeams(teamId),
          this._entities.inboxesStore.refreshIntegrationTeamContacts(teamId),
        ])
      )
      await this.loadData()
      showToast({
        type: 'success',
        title: 'HubSpot data refreshed',
      })
    } catch (error) {
      showToast({ type: 'error', title: 'Refresh from HubSpot failed' })
      logger.error(error)
    } finally {
      runInAction(() => {
        this._resyncingIntegrationTeams = false
      })
    }
  }

  get renameTeamName(): string {
    return this._renameTeamName
  }

  get renameTeamDescription(): string {
    return this._renameTeamDescription
  }

  get renameTeamLoading(): boolean {
    return this._renameTeamLoading
  }

  get renameTeamCanSave(): boolean {
    const snapshot = this._renameTeam
    if (!snapshot) return false

    const nameTrimmed = this._renameTeamName.trim()
    const descTrimmed = this._renameTeamDescription.trim() || null
    const hasChanges =
      nameTrimmed !== snapshot.name || descTrimmed !== (snapshot.description ?? null)
    return nameTrimmed.length > 0 && hasChanges && !this._renameTeamLoading
  }

  setRenameTeamName = (value: string): void => {
    this._renameTeamName = value
  }

  setRenameTeamDescription = (value: string): void => {
    this._renameTeamDescription = value
  }

  closeRenameTeamModal = (): void => {
    modalStore.removeModal(this._renameTeamModalId)
    runInAction(() => {
      this._renameTeam = null
    })
  }

  handleRenameTeamSave = async (): Promise<void> => {
    if (!this._renameTeam) return

    this._renameTeamLoading = true

    try {
      await this.handleRenameTeam(
        this._renameTeamName.trim(),
        this._renameTeamDescription.trim() || null
      )

      this.closeRenameTeamModal()
    } finally {
      runInAction(() => {
        this._renameTeamLoading = false
      })
    }
  }

  handleRequestRenameTeam = (team: Team): void => {
    this._renameTeam = team
    this._renameTeamName = team.name
    this._renameTeamDescription = team.description ?? ''
    this._renameTeamLoading = false

    modalStore.addModal({
      id: this._renameTeamModalId,
      type: ModalTypeList.DEFAULT,
      title: 'Rename team',
      ModalContent: RenameTeamModalContent,
      ModalActions: RenameTeamModalActions,
      showCloseButton: false,
      paddingContent: '6px 24px 16px 24px',
      ModalContentProps: {
        store: this,
      },
    })
  }

  handleRenameTeam = async (name: string, description: string | null): Promise<void> => {
    try {
      const team = this._renameTeam
      if (!team) return

      const memberIds = team.members.map((m) => m.id)
      const { data } = await this._entities.inboxesStore.updateGroup({
        group_id: team.id,
        name,
        description,
        members: memberIds,
      })

      runInAction(() => {
        this.setItem(data.data)
        if (this._currentTeam?.id === data.data.id) {
          this._currentTeam = new Team(data.data)
        }
      })
      showToast({ type: 'success', title: 'Team renamed' })
    } catch (error) {
      showToast({ type: 'error', title: 'Error updating team' })
      logger.error(error)
    }
  }

  handleRemoveMember = async (memberId: number): Promise<void> => {
    const teamId = this.currentTeamId
    if (!teamId) return

    try {
      const { data } = await this._entities.inboxesStore.removeGroupMembers(teamId, [memberId])

      runInAction(() => {
        this._currentTeam = new Team(data.data)
      })

      const toastId = showToast({
        type: 'success',
        title: 'Member removed',
        action: {
          text: 'Undo',
          onAction: () => {
            toastStore.remove(toastId)
            this.untrackManageMembersToastId(toastId)
            void this.handleUndoRemoveMember(teamId, memberId)
          },
        },
      })
      this.trackManageMembersToastId(toastId)
    } catch (error) {
      showToast({ type: 'error', title: 'Error removing member' })
      logger.error(error)
    }
  }

  handleRemoveMembers = async (teamId: number, memberIds: number[]): Promise<void> => {
    if (memberIds.length === 0 || !Number.isFinite(teamId)) return
    try {
      const { data } = await this._entities.inboxesStore.removeGroupMembers(teamId, memberIds)

      runInAction(() => {
        this._currentTeam = new Team(data.data)
      })

      this._membersTableStore.bulkStore.resetSelected()
      const toastId = showToast({
        type: 'success',
        title: memberIds.length > 1 ? `${memberIds.length} members removed` : 'Member removed',
        action: {
          text: 'Undo',
          onAction: () => {
            toastStore.remove(toastId)
            this.untrackManageMembersToastId(toastId)
            void this.handleUndoRemoveMembers(teamId, memberIds)
          },
        },
      })
      this.trackManageMembersToastId(toastId)
    } catch (error) {
      showToast({ type: 'error', title: 'Error removing members' })
      logger.error(error)
    }
  }

  handleUndoRemoveMember = async (teamId: number, memberId: number): Promise<void> => {
    try {
      const { data } = await this._entities.inboxesStore.addGroupMembers(teamId, [memberId])

      runInAction(() => {
        this._currentTeam = new Team(data.data)
      })
    } catch (error) {
      showToast({ type: 'error', title: 'Error adding member back' })
      logger.error(error)
    }
  }

  handleUndoRemoveMembers = async (teamId: number, memberIds: number[]): Promise<void> => {
    if (memberIds.length === 0) return
    try {
      const { data } = await this._entities.inboxesStore.addGroupMembers(teamId, memberIds)

      runInAction(() => {
        this._currentTeam = new Team(data.data)
      })
    } catch (error) {
      showToast({ type: 'error', title: 'Error adding members back' })
      logger.error(error)
    }
  }

  handleImportFromHubSpot = () => {
    const hubspotIntegration = this._entities.integrationsStore.getIntegration(
      IntegrationKey.hubspot
    )
    const isConnected = hubspotIntegration?.isConnected ?? false

    if (!isConnected) {
      const hubspotSettingsPath = `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${IntegrationKey.hubspot}/${IntegrationsRoutes.settings}`

      uiStore.changeRouteViaVue({ path: hubspotSettingsPath })
      return
    }

    if (!this._integrationTeams.length) {
      modalStore.addModal({
        id: this._noTeamsToImportModalId,
        type: ModalTypeList.FAIL,
        title: 'No teams to import',
        desc: "We couldn't find any teams available to import from HubSpot. You can create a new team in Salesmsg, or add teams in HubSpot and try again later.",
        primaryAction: {
          text: 'Create new team',
          onAction: () => {
            modalStore.removeModal(this._noTeamsToImportModalId)
            this.navigateToCreateTeam()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            modalStore.removeModal(this._noTeamsToImportModalId)
          },
        },
      })
    }
  }

  dispose = () => {
    this._tableStore.dispose()
    this._disposeReaction?.()
    this._requestLoadData.dispose()
  }
}
